// src/composables/useTheme.ts
type Theme = 'default' | 'dark' | 'auto'
const STORE_KEY = 'hs_theme'

function applyHtmlClass(theme: Exclude<Theme, 'auto'>) {
  const html = document.documentElement
  html.classList.remove('dark', 'default', 'auto')
  html.classList.add(theme)
}

function systemPrefersDark() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

function resolveTheme(theme: Theme): Exclude<Theme, 'auto'> {
  if (theme === 'auto') return systemPrefersDark() ? 'dark' : 'default'
  return theme
}

export function useTheme() {
  const getStored = (): Theme =>
    (localStorage.getItem(STORE_KEY) as Theme) || 'default'

  const get = (): Exclude<Theme, 'auto'> => resolveTheme(getStored())

  const set = (theme: Theme, persist = true, dispatch = true) => {
    // desactiva transiciones brevemente
    const s = document.createElement('style')
    s.setAttribute('data-hs-appearance-onload-styles', '')
    s.textContent = `*{transition: none !important}`
    document.head.appendChild(s)

    if (persist) localStorage.setItem(STORE_KEY, theme)

    applyHtmlClass(resolveTheme(theme))

    requestAnimationFrame(() => s.remove())

    if (dispatch) {
      window.dispatchEvent(new CustomEvent('on-hs-appearance-change', { detail: theme }))
    }
  }

  const init = () => {
    // no pisar si ya hay .dark pre-render (por ejemplo SSR)
    if (document.documentElement.classList.contains('dark')) return
    set(getStored(), false, false)
  }

  const onSystemChange = () => {
    if (getStored() === 'auto') set('auto', false)
  }

  // engancha listener a prefers-color-scheme
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  media?.addEventListener?.('change', onSystemChange)

  return { get, set, init }
}